import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from "@/app/store";
import { AuthState, AuthResponse, AuthChargeData, MenuItem, MenuPagesMap, MenuMap, PagePermissionMap } from './auth.interface';
import * as authThunks from './authThunks';
import { APIResponse } from '@/types/custom-http';

const buildMenuState = (menu: MenuItem[]) => {
  const menuMap: MenuMap = {};
  const menuPagesMap: MenuPagesMap = {};
  const pagePermissionMap: PagePermissionMap = {};

  menu.forEach((item) => {
    menuMap[item.code] = item;
    menuPagesMap[item.code] = item.pages ?? [];

    (item.pages ?? []).forEach((page) => {
      pagePermissionMap[page.code] = page;
    });
  });

  return { menuMap, menuPagesMap, pagePermissionMap };
};

const initialState: AuthState = {
  user: null,
  userGroup: null,
  isAuthenticated: false,
  menu: [],
  menuMap: {},
  menuPagesMap: {},
  pagePermissionMap: {},
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    authSuccess: (state, action: PayloadAction<AuthChargeData>) => {
      state.user = action.payload.user;
      state.menu = action.payload.menu;
      state.userGroup = action.payload.grupo;
      Object.assign(state, buildMenuState(action.payload.menu));
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    authFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isAuthenticated = false;
      state.loading = false;
    },
    clearAuth: (state) => {
      state.user = null;
      state.userGroup = null;
      state.menu = [];
      state.menuMap = {};
      state.menuPagesMap = {};
      state.pagePermissionMap = {};
      state.isAuthenticated = false;
      state.error = null;
      state.loading = false;
    },
    authCheckComplete: (state) => {
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(authThunks.login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authThunks.login.fulfilled, (state, action: PayloadAction<APIResponse<AuthResponse>>) => {
        const menu = action.payload.data?.menu ?? [];
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.data?.payload.user ?? null;
        state.userGroup = action.payload.data?.payload.grupo ?? null;
        state.menu = menu;
        Object.assign(state, buildMenuState(menu));
      })
      .addCase(authThunks.login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload?.message as string;
      })
      .addCase(authThunks.logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(authThunks.logout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.userGroup = null;
        state.menu = [];
        state.menuMap = {};
        state.menuPagesMap = {};
        state.pagePermissionMap = {};
      })
      .addCase(authThunks.logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
  },
});

export const selectUser = (state: RootState) => state.auth.user;
export const selectUserGroup = (state: RootState) => state.auth.userGroup;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectMenu = (state: RootState) => state.auth.menu;
export const selectMenuMap = (state: RootState) => state.auth.menuMap;
export const selectMenuPagesMap = (state: RootState) => state.auth.menuPagesMap;
export const selectPagePermissionMap = (state: RootState) => state.auth.pagePermissionMap;

export const { clearAuthError, authSuccess, authFailed, authCheckComplete, clearAuth } = authSlice.actions;

export default authSlice.reducer;
